import { ContentListing, type ContentListingChip } from '@mdvip/shared/components/blocks/ContentListing';
import { FeaturedContentCard } from '@mdvip/shared/components/blocks/FeaturedContentCard';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { useMyBookmarkedEntityIds } from '@mdvip/shared/features/bookmarks/useMyBookmarkedEntityIds';
import { useDebouncedValue } from '@mdvip/shared/hooks/useDebouncedValue';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useToggleWellnessArticleBookmark } from './useToggleWellnessArticleBookmark';
import { useWellnessArticles } from './useWellnessArticles';
import type { WellnessArticle } from './wellnessApi';
import { toWellnessArticleCard } from './wellnessArticleCard.mapper';

const PAGE_SIZE = 9;
const FEATURED_COUNT = 1;
const PAGE_PARAM = 'page';
const SEARCH_PARAM = 'search';
const SEARCH_DEBOUNCE_MS = 300;

function parsePage(raw: string | null): number {
  const page = Number(raw);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

/**
 * With no search, the newest article is featured above the grid, so the grid starts one row in and
 * its count excludes it. A search drops the featured card and pages the full match set.
 */
function wellnessGridWindow(page: number, search: string) {
  const clean = !search;
  return {
    offset: (clean ? FEATURED_COUNT : 0) + (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    search: search || undefined,
  };
}

function wellnessGridCount(totalCount: number, search: string): number {
  return search ? totalCount : Math.max(0, totalCount - FEATURED_COUNT);
}

export interface WellnessArticlesPageProps {
  /** App chrome above the title lockup (the members app passes its Wellbeing subnav). */
  above?: React.ReactNode;
}

/**
 * The Wellness Articles page: the daily HealthDay feed, newest first, as one featured article over
 * a paginated grid, with keyword search. `page` and `search` live in the URL, so a reload, a deep
 * link and browser Back all land on the same view.
 */
export function WellnessArticlesPage({ above }: WellnessArticlesPageProps) {
  const { t } = useTranslation('shared');
  const tError = useErrorTranslation();
  const { formatDateWithWeekday } = useFormatDate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePage(searchParams.get(PAGE_PARAM));
  const search = searchParams.get(SEARCH_PARAM) ?? '';
  const clean = !search;

  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    // Same guard as RecipesPage: right after an immediate clear, `debouncedSearch` still holds the
    // pre-clear term, and committing it would write the cleared search straight back.
    if (debouncedSearch !== searchInput) return;
    if (debouncedSearch === search) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (debouncedSearch) next.set(SEARCH_PARAM, debouncedSearch);
        else next.delete(SEARCH_PARAM);
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, searchInput, search, setSearchParams]);

  const featuredQuery = useWellnessArticles({ offset: 0, limit: FEATURED_COUNT }, { enabled: clean });
  const gridQuery = useWellnessArticles(wellnessGridWindow(page, search));

  const featured = clean ? featuredQuery.data?.edges[0] : undefined;
  const gridArticles = gridQuery.data?.edges;
  const totalCount = gridQuery.data?.totalCount;
  const failed = gridQuery.isError ? gridQuery.error : clean && featuredQuery.isError ? featuredQuery.error : null;

  // Held back until the grid is fresh (not the previous page kept on screen) and the featured query
  // has settled, so a screenful costs exactly one saved-state check rather than one per arrival.
  const screenSettled = gridQuery.isSuccess && !gridQuery.isPlaceholderData && (!clean || !featuredQuery.isPending);
  const screenIds = useMemo(
    () =>
      screenSettled ? [...(featured ? [featured.id] : []), ...(gridArticles ?? []).map((article) => article.id)] : [],
    [screenSettled, featured, gridArticles],
  );
  const savedIds = useMyBookmarkedEntityIds('WELLNESS_ARTICLE', screenIds);
  const { toggle: toggleBookmark } = useToggleWellnessArticleBookmark();

  const toCard = (article: WellnessArticle) => ({
    ...toWellnessArticleCard(article, formatDateWithWeekday, savedIds),
    onSaveChange: (next: boolean) => toggleBookmark({ entityId: article.id, title: article.title, next }),
  });

  const handlePageChange = (nextPage: number, options?: { replace?: boolean }) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(PAGE_PARAM, String(nextPage));
        return next;
      },
      { replace: options?.replace ?? false },
    );
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(SEARCH_PARAM);
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  const handleRetry = () => {
    void gridQuery.refetch();
    if (clean) void featuredQuery.refetch();
  };

  const extraChips: ContentListingChip[] = search
    ? [
        {
          key: 'search',
          icon: 'search',
          label: t('wellnessArticles.searchChip', { term: search }),
          srLabel: t('wellnessArticles.clearSearch'),
          onRemove: handleClearSearch,
        },
      ]
    : [];

  const noContent = clean && totalCount === 0;
  const onlyFeatured = clean && totalCount === FEATURED_COUNT;

  const featuredCard = featured ? (
    <FeaturedContentCard
      {...toCard(featured)}
      kicker={t('wellnessArticles.featured.kicker')}
      ctaLabel={t('wellnessArticles.featured.cta')}
    />
  ) : null;

  const belowHead = (
    <div className="flex flex-col gap-6">
      <div className="inline-flex items-start gap-3 self-start rounded-lg border bg-muted p-4">
        <Icon name="info" size="sm" className="mt-0.5 text-muted-foreground" />
        <p className="text-sm leading-relaxed text-muted-foreground desktop:whitespace-nowrap">
          {t('wellnessArticles.disclaimer')}
        </p>
      </div>
      {featuredCard}
    </div>
  );

  if (failed) {
    return (
      <div className="flex flex-col gap-8">
        {above}
        <Alert variant="destructive">
          <Icon name="alertCircle" />
          <AlertTitle>{tError('title')}</AlertTitle>
          <AlertDescription>
            <p>{tError(extractGqlErrorCode(failed))}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={handleRetry}>
              {t('errors.tryAgain')}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <ContentListing
      noun={t('wellnessArticles.noun')}
      nounSingular={t('wellnessArticles.nounSingular')}
      title={t('wellnessArticles.title')}
      dek={t('wellnessArticles.dek')}
      above={above}
      belowHead={belowHead}
      searchLabel={t('wellnessArticles.searchPlaceholder')}
      q={searchInput}
      onQChange={setSearchInput}
      cards={(gridArticles ?? []).map(toCard)}
      filteredCount={wellnessGridCount(totalCount ?? 0, search)}
      loading={gridQuery.isPending}
      emptyIcon="newspaper"
      emptyTitle={
        noContent
          ? t('wellnessArticles.empty.noContent.title')
          : t('wellnessArticles.empty.noResults.title', { term: search })
      }
      emptyHint={noContent ? t('wellnessArticles.empty.noContent.hint') : t('wellnessArticles.empty.noResults.hint')}
      emptyActionLabel={search ? t('wellnessArticles.empty.noResults.action') : undefined}
      onEmptyAction={search ? handleClearSearch : undefined}
      pageSize={PAGE_SIZE}
      page={page}
      onPageChange={handlePageChange}
      extraChips={extraChips}
      onClearAll={handleClearSearch}
      showResults={!onlyFeatured}
    />
  );
}
